import type { ReportSection } from "./domain";
import { resolveSectionTenLineageState } from "./section10-lineage";

export type SectionProgressLabel =
  | "Not started"
  | "Evidence in progress"
  | "Assessment in progress"
  | "Formatting needs review"
  | "Client draft required"
  | "Client draft awaiting approval"
  | "Ready to complete"
  | "Ready"
  | "Complete"
  | "Stale";

type NativeReadiness = { ready: boolean; missing: string[] };

export interface SectionProgressResult {
  label: SectionProgressLabel;
  completionRecorded: boolean;
  complete: boolean;
  stale: boolean;
  hasClientVisibleOutput: boolean;
  eligibleForSectionTen: boolean;
}

const workflows = (section: ReportSection) => [
  section.sectionOneWorkflow,
  section.sectionTwoWorkflow,
  section.sectionThreeWorkflow,
  section.sectionFourWorkflow,
  section.sectionFiveWorkflow,
  section.sectionSixWorkflow,
  section.sectionSevenWorkflow,
  section.sectionEightWorkflow,
  section.sectionNineWorkflow,
  section.sectionTenWorkflow,
].filter(Boolean) as Array<Record<string, unknown>>;

const legacyCompletionRecorded = (section: ReportSection) =>
  section.status === "Ready for review" || section.status === "Approved for report";

const simplifiedCompletionRecorded = (section: ReportSection) => {
  if (section.id === "commercial-gaps") return Boolean(section.sectionSixWorkflow?.completedAt);
  if (section.id === "competitors" && section.sectionSevenWorkflow?.simplifiedVersion) return Boolean(section.sectionSevenWorkflow.completedAt);
  if (section.id === "authority-gaps" && section.sectionEightWorkflow?.simplifiedVersion) return Boolean(section.sectionEightWorkflow.completedAt);
  if (section.id === "positioning" && section.sectionNineWorkflow?.simplifiedVersion) return Boolean(section.sectionNineWorkflow.completedAt);
  if (section.id === "work-programme" && section.sectionTenWorkflow?.redesignVersion === 2) return resolveSectionTenLineageState(section.sectionTenWorkflow).completionCurrent;
  return false;
};

export function sectionCompletionRecordedForApproval(section:ReportSection) {
  return legacyCompletionRecorded(section) || simplifiedCompletionRecorded(section);
}

const hasClientVisibleOutput = (section: ReportSection) =>
  (section.blocks ?? []).some(block => block.clientVisible && block.content.trim()) ||
  Boolean(section.sectionOneWorkflow?.clientDraft && section.sectionOneWorkflow.clientDraftApproved) ||
  Boolean(section.sectionTwoWorkflow?.clientDraft && section.sectionTwoWorkflow.clientDraftApproved) ||
  Boolean(section.sectionThreeWorkflow?.clientDraft && section.sectionThreeWorkflow.clientDraftApproved);

export function deriveSectionProgressResult(section: ReportSection, readiness: NativeReadiness): SectionProgressResult {
  const records = workflows(section);
  const sectionTenV2 = section.sectionTenWorkflow?.redesignVersion === 2 ? section.sectionTenWorkflow : undefined;
  const sectionTenLineage=sectionTenV2?resolveSectionTenLineageState(sectionTenV2):undefined;
  const sectionTenClientStale = Boolean(sectionTenV2?.v2ClientDraft&&sectionTenLineage?.draftRelationship!=="Current");
  const stale = sectionTenClientStale || readiness.missing.some(item => /stale|changed|refresh|latest|newer/i.test(item));
  const legacyCompleted = legacyCompletionRecorded(section);
  const simplifiedCompleted = simplifiedCompletionRecorded(section);
  const completionRecorded = legacyCompleted || simplifiedCompleted;
  const complete = completionRecorded && readiness.ready && !stale;
  const approved = sectionTenLineage?.approvalCurrent??records.some(item => item.clientDraftApproved === true || item.simplifiedClientDraftApproved === true || item.v2ClientDraftApproved === true);
  const hasClientDraft = records.some(item => Boolean(item.clientDraft || item.simplifiedClientDraft || item.v2ClientDraft));
  const confirmed = records.some(item => Boolean(
    item.confirmedAssessment ||
    item.confirmedNarrative ||
    item.confirmedNarrativeAssessment ||
    item.simplifiedConfirmedAssessment ||
    item.v2ConfirmedAssessment
  ));
  const assessmentStarted = records.some(item => Boolean(
    item.workingAssessment ||
    item.simplifiedWorkingAssessment ||
    item.v2WorkingAssessment ||
    item.narrativeDraft ||
    item.currentDraftId ||
    item.assessmentUpdatedAt
  ));
  const evidenceStarted = Boolean(
    section.body?.trim() ||
    section.evidence?.length ||
    section.blocks?.length ||
    records.some(item => Object.values(item).some(value => Array.isArray(value) && value.length > 0))
  );
  let label: SectionProgressLabel;
  if (stale) label = "Stale";
  else if (complete) label = simplifiedCompleted ? "Complete" : "Ready";
  else if (approved && readiness.ready) label = "Ready to complete";
  else if (hasClientDraft && !approved) label = "Client draft awaiting approval";
  else if (confirmed) label = "Client draft required";
  else if (assessmentStarted) label = "Assessment in progress";
  else label = evidenceStarted ? "Evidence in progress" : "Not started";
  const clientOutput = hasClientVisibleOutput(section);
  return {
    label,
    completionRecorded,
    complete,
    stale,
    hasClientVisibleOutput: clientOutput,
    eligibleForSectionTen: complete && clientOutput,
  };
}

export function deriveSectionProgress(section: ReportSection, readiness: NativeReadiness): SectionProgressLabel {
  return deriveSectionProgressResult(section, readiness).label;
}
